import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const strings = {
  en: {
    // Header
    help: 'Help',
    signIn: 'Sign in',
    signOut: 'Log out',
    dashboard: 'Dashboard',
    adminPanel: 'Admin panel',
    menu: 'Menu',
    // Nav / Common links
    aboutUs: 'About us',
    privacyPolicy: 'Privacy policy',
    refundPolicy: 'Refund policy',
    termsConditions: 'Terms & conditions',
    shippingReturn: 'Shipping & return',
    contactUs: 'Contact us',
    backToHome: 'Back to home',
    backToSignIn: 'Back to sign in',
    // Footer
    footerRights: '© 2025 Myhealthyfy. All rights reserved.',
    footerDisclaimer: 'This site does not provide medical advice. Always consult a healthcare professional.',
    lastUpdated: 'Last updated',
    needHelp: 'Need help? Contact us at',
    // Landing Page
    landingHero1: 'BUILD YOUR',
    landingHero2: 'PERFECT BODY',
    landingSub: 'Complete this quiz and enroll in the Myhealthyfy subscription',
    selectYourAge: 'SELECT YOUR AGE',
    downloads: 'Downloads',
    appRating: 'App rating',
    successRate: 'Success rate',
    reviews: 'Reviews',
    landingTermsPre: 'By continuing, you agree to our',
    landingTermsAnd: 'and',
    landingTermsMeta: '(including the use of tracking technologies, e.g. Meta Pixel)',
    landingMedical: 'This site does not provide medical advice. Always consult a healthcare professional with any health concerns',
    // Quiz
    continue: 'Continue',
    // Loading Page
    buildingPlan1: 'BUILDING YOUR',
    buildingPlan2: 'PERSONALIZED PLAN',
    buildingPlanSub: 'Please wait while we craft your program',
    task1: 'Analyzing your answers',
    task2: 'Calculating your BMI',
    task3: 'Building your workout schedule',
    task4: 'Personalizing your nutrition plan',
    task5: 'Preparing your dashboard',
    // Plan Page
    yourPlanIsReady: 'YOUR PLAN IS READY',
    personalWorkoutPlan: 'PERSONAL WORKOUT PLAN',
    basedOnAnswers: 'BASED ON YOUR ANSWERS',
    yourForecast: 'Your forecast',
    reachBy: 'Reach',
    byWeek12: 'by week 12',
    totalLoss: 'Total loss',
    week: 'Week',
    whatYoullGet: "What you'll get",
    chooseYourPlan: 'Choose your plan',
    monthPlan1: '1-Month Plan',
    monthPlan3: '3-Month Plan',
    monthPlan6: '6-Month Plan',
    mostPopular: 'MOST POPULAR',
    bestValue: 'BEST VALUE',
    perDay: 'per day',
    total: 'Total',
    getMyPlan: 'Get my plan',
    signInToContinue: 'Sign in to continue',
    processing: 'Processing...',
    moneyBack: '30-day money-back guarantee',
    peopleLove: 'People love Myhealthyfy',
    retakeQuiz: 'Retake the quiz',
    youreIn: "YOU'RE IN!",
    activated: 'activated',
    validUntil: 'Valid until',
    goToDashboard: 'Go to dashboard',
    paymentSimulated: 'Payment simulated (Razorpay keys not configured). Real charges will apply once keys are added.',
    paymentFailedPrefix: 'Payment failed:',
    // Feature list
    featurePersonalWorkout: 'Personalized workout plan',
    featureCustomNutrition: 'Custom nutrition guide',
    featureProgressTracking: 'Progress tracking',
    featureTrainerSupport: 'Expert trainer support',
    featureAccess: '24/7 access to workouts',
    featureCommunity: 'Community & motivation',
    // Auth
    createAccount: 'CREATE ACCOUNT',
    welcomeBack: 'WELCOME BACK',
    saveYourPlan: 'Save your plan and track progress',
    signInToView: 'Sign in to view your plan',
    continueWithGoogle: 'Continue with Google',
    or: 'or',
    namePlaceholder: 'Name',
    emailPlaceholder: 'Email',
    passwordPlaceholder: 'Password',
    forgotPassword: 'Forgot password?',
    pleaseWait: 'Please wait...',
    signInBtn: 'Sign in',
    createAccountBtn: 'Create account',
    alreadyHaveAccount: 'Already have an account?',
    newHere: 'New here?',
    somethingWentWrong: 'Something went wrong',
    // Forgot / Reset password
    forgotTitle: 'FORGOT PASSWORD?',
    forgotSub: "Enter your email and we'll open a secure reset page for you.",
    yourEmailPlaceholder: 'Your account email',
    rememberedIt: 'Remembered it?',
    readyToReset: 'Ready to reset',
    resetPrepared1: 'If an account with',
    resetPrepared2: "exists and uses a password (not Google sign-in), we've prepared a one-hour secure link below.",
    googleNoPassword: 'If you signed up with Google, please use "Continue with Google" on the sign-in page instead — Google accounts have no password.',
    resetLinkExpiry: 'This link expires in 1 hour and can be used only once.',
    resetMyPassword: 'Reset my password',
    resetPasswordTitle: 'RESET PASSWORD',
    resetPasswordSub: 'Choose a new password for your account.',
    newPasswordPlaceholder: 'New password',
    confirmPasswordPlaceholder: 'Confirm new password',
    passwordUpdated: 'PASSWORD UPDATED',
    redirectingSignIn: 'Redirecting to sign-in...',
    signInNow: 'Sign in now',
    updatePassword: 'Update password',
    updating: 'Updating...',
    orBack: 'Or',
    backToSignInLink: 'back to sign in',
    resetLinkMissingToken: 'This reset link is missing a token. Please request a new one.',
    passwordMin6: 'Password must be at least 6 characters.',
    passwordsDontMatch: 'Passwords do not match.',
    resetPasswordError: 'Could not reset password. Please try again.',
    // Dashboard
    welcome: 'Welcome back',
    yourSubscription: 'Your subscription',
    active: 'ACTIVE',
    none: 'NONE',
    renewsOn: 'Renews on',
    paymentMock: 'Payment was MOCK (Razorpay not configured)',
    noActivePlan: 'No active plan yet.',
    takeQuizToGetOne: 'Take the quiz to get one',
    yourPlan: 'Your plan',
    retakeQuizShort: 'Retake quiz',
    loading: 'Loading...',
    ageGroup: 'Age group',
    gender: 'Gender',
    bodyType: 'Body type',
    goal: 'Goal',
    currentWeight: 'Current weight',
    targetWeight: 'Target weight',
    targetReduction: 'Target reduction',
    noQuizOnFile: 'No quiz on file.',
    takeTheQuiz: 'Take the quiz',
    unlockPlan: 'UNLOCK YOUR 30-DAY MEAL PLAN',
    unlockPlanSub: 'Purchase a subscription to reveal your personalized targets, weekly meals, and workout guidance.',
    unlockFeature1: 'Personalized calorie & protein targets',
    unlockFeature2: '30 days × up to 8 meals per day (veg + non-veg)',
    unlockFeature3: 'Weight loss and weight gain plans',
    purchasePlan: 'Purchase plan',
    startsAt: 'Starts at ₹300 · 30-day money-back guarantee',
    // MealPlan
    weightLoss: 'Weight loss',
    weightGain: 'Weight gain',
    veg: 'Veg',
    nonVeg: 'Non-veg',
    avgLabel: 'Avg',
    kcalPerDay: 'kcal/day',
    yourDailyTargets: 'Your personalized daily targets',
    calories: 'Calories',
    protein: 'Protein',
    water: 'Water',
    activity: 'Activity',
    kcalPerDayShort: 'kcal/day',
    gPerDay: 'g/day',
    litresPerDay: 'litres/day',
    calculatedFrom: 'Calculated from your profile:',
    prevWeek: 'Previous week',
    nextWeek: 'Next week',
    day: 'Day',
    meals: 'meals',
    dailyTipsFor: 'Daily tips for your',
    weightLossJourney: 'weight loss journey',
    weightGainJourney: 'weight gain journey',
    workoutGuide: 'Workout guide',
    workoutGuideCaption: 'Tap the image to open full-size. Pair this 5-day split with your meal plan for best results.',
    workoutGuideAlt: '5-day workout plan: chest, back, shoulders, arms, legs',
    mealPlanDisclaimer: 'This meal plan is a general guide, not medical advice. Portion sizes and macros should be adjusted to your body weight, activity level, and any dietary restrictions. Consult a registered dietitian for a fully personalised plan.',
    // Contact Us
    contactPre: "WE'D LOVE TO HEAR FROM YOU",
    contactTitle: 'CONTACT US',
    contactSub: "Questions, feedback, partnerships, or just saying hi — drop us a message and we'll get back within 1–2 business days.",
    messageSent: 'MESSAGE SENT!',
    messageSentSub: "Thanks for reaching out. We'll reply to your email within 1–2 business days.",
    sendAnother: 'Send another message',
    sendUsMessage: 'Send us a message',
    yourNamePlaceholder: 'Your name',
    emailAddressPlaceholder: 'Email address',
    subject: 'Subject',
    subjectGeneral: 'General inquiry',
    subjectSupport: 'Technical support',
    subjectBilling: 'Billing / refund',
    subjectFeedback: 'Feedback / feature request',
    subjectPartnership: 'Partnership / press',
    subjectOther: 'Other',
    howCanWeHelp: 'How can we help?',
    fillRequired: 'Please fill in your name, email and message.',
    couldNotSend: 'Could not send your message. Please try again.',
    sending: 'Sending...',
    sendMessage: 'Send message',
    submitAgree: 'By submitting, you agree to our',
    emailUs: 'Email us',
    phone: 'Phone',
    phoneHours: 'Mon–Fri, 9am–6pm',
    address: 'Address',
    responseTime: 'Response time',
    within12: 'Within 1–2 business days',
    priorityFor: 'Priority for active subscribers',
    faqTitle: 'Frequently asked',
    // Language labels
    languageEn: 'EN',
    languageHi: 'हिं',
    // Quiz sub-steps
    height: 'Height',
    weight: 'Weight',
    currentWeightLabel: 'Current weight:',
    enterTargetWeight: 'Enter target weight in',
    enterYourAge: 'Enter your age',
    // Pregnancy plan
    pregnancy: 'Pregnancy',
    pregnancyDietPlan: '9-month pregnancy diet plan',
    pregnancyDietSubtitle: 'Monthly nutrition guide for a healthy pregnancy',
    monthLabel: 'Month',
    focusLabel: 'Focus',
    dailySchedule: 'Daily meal schedule',
    keyRecommendations: 'Key foods to include',
    foodsToAvoidTitle: 'Foods to avoid',
    pregnancyDisclaimerLabel: 'Medical note',
    unlockFeature4: '9-month pregnancy diet with month-by-month guidance',
    // Wearables
    wearablesTitle: 'Wearable integrations',
    wearablesSubtitle: 'Sync your fitness data automatically — coming soon',
    comingSoon: 'Coming soon',
    connect: 'Connect',
    wearableAppleHealth: 'Apple Health',
    wearableGoogleHealth: 'Google Health Connect',
    wearableFitbit: 'Fitbit',
    wearableGarmin: 'Garmin',
    wearableSamsungHealth: 'Samsung Health',
    wearableDisclaimer: 'Wearable integrations are on our roadmap. You will be able to sync steps, heart rate, calories & sleep directly to your dashboard.',
    // AI meal tracker
    trackMealTitle: 'AI photo calorie counter',
    trackMealSub: 'Snap or upload your meal — AI estimates calories & macros in seconds',
    uploadPhoto: 'Upload photo',
    takePhoto: 'Take photo',
    analyzing: 'Analyzing your meal...',
    analyzeAgain: 'Analyze again',
    saveMeal: 'Save to log',
    cancel: 'Cancel',
    detected: 'Detected meal',
    ingredients: 'Ingredients',
    portion: 'Portion',
    fatShort: 'Fat',
    carbs: 'Carbs',
    confidence: 'AI confidence',
    todaysMeals: "Today's meals",
    dailyTotals: 'Daily totals',
    noMealsToday: 'No meals logged today. Snap your first meal above!',
    delete: 'Delete',
    mealSaved: 'Meal saved to your log',
    analyzeFailed: 'Could not analyse the image. Please try a clearer photo.',
    noFoodDetected: 'No food detected. Please try a clearer photo of your meal.',
    imageTooLarge: 'Image is too large. Please use one under 5 MB.',
    unlockFeature5: 'AI photo calorie counter (snap & log meals)',
    // Diabetic plan
    diabetic: 'Diabetic',
    diabeticDietPlan: '30-day diabetic (sugar-friendly) diet plan',
    diabeticDietSubtitle: 'Low-sugar, high-fiber meals for blood-sugar control',
    weekRotations: 'Weekly rotations (weeks 2–4)',
    foodsToInclude: 'Foods to include',
    lifestyleTitle: 'Lifestyle tips',
    unlockFeature6: '30-day diabetic diet plan (low-sugar, doctor-friendly)',
  },
  hi: {
    // Header
    help: 'सहायता',
    signIn: 'साइन इन करें',
    signOut: 'लॉग आउट',
    dashboard: 'डैशबोर्ड',
    adminPanel: 'एडमिन पैनल',
    menu: 'मेनू',
    // Nav / Common links
    aboutUs: 'हमारे बारे में',
    privacyPolicy: 'गोपनीयता नीति',
    refundPolicy: 'रिफंड नीति',
    termsConditions: 'नियम व शर्तें',
    shippingReturn: 'शिपिंग व वापसी',
    contactUs: 'संपर्क करें',
    backToHome: 'होम पर वापस जाएँ',
    backToSignIn: 'साइन इन पर वापस',
    // Footer
    footerRights: '© 2025 Myhealthyfy। सर्वाधिकार सुरक्षित।',
    footerDisclaimer: 'यह साइट चिकित्सा सलाह प्रदान नहीं करती है। हमेशा स्वास्थ्य विशेषज्ञ से परामर्श लें।',
    lastUpdated: 'अंतिम अद्यतन',
    needHelp: 'मदद चाहिए? हमसे संपर्क करें',
    // Landing Page
    landingHero1: 'अपनी परफेक्ट',
    landingHero2: 'बॉडी बनाएं',
    landingSub: 'यह क्विज़ पूरा करें और Myhealthyfy सदस्यता लें',
    selectYourAge: 'अपनी उम्र चुनें',
    downloads: 'डाउनलोड',
    appRating: 'ऐप रेटिंग',
    successRate: 'सफलता दर',
    reviews: 'समीक्षाएँ',
    landingTermsPre: 'आगे बढ़कर, आप हमारी',
    landingTermsAnd: 'और',
    landingTermsMeta: 'से सहमत हैं (ट्रैकिंग तकनीकों जैसे Meta Pixel के उपयोग सहित)',
    landingMedical: 'यह साइट चिकित्सा सलाह नहीं देती। किसी भी स्वास्थ्य समस्या के लिए हमेशा योग्य स्वास्थ्य विशेषज्ञ से सलाह लें।',
    // Quiz
    continue: 'जारी रखें',
    // Loading Page
    buildingPlan1: 'आपका व्यक्तिगत',
    buildingPlan2: 'प्लान तैयार हो रहा है',
    buildingPlanSub: 'कृपया प्रतीक्षा करें, हम आपका प्रोग्राम बना रहे हैं',
    task1: 'आपके उत्तरों का विश्लेषण किया जा रहा है',
    task2: 'आपका BMI कैलकुलेट किया जा रहा है',
    task3: 'आपका वर्कआउट शेड्यूल बनाया जा रहा है',
    task4: 'आपका पोषण प्लान तैयार किया जा रहा है',
    task5: 'आपका डैशबोर्ड तैयार किया जा रहा है',
    // Plan Page
    yourPlanIsReady: 'आपका प्लान तैयार है',
    personalWorkoutPlan: 'व्यक्तिगत वर्कआउट प्लान',
    basedOnAnswers: 'आपके उत्तरों पर आधारित',
    yourForecast: 'आपका पूर्वानुमान',
    reachBy: 'हासिल करें',
    byWeek12: '12वें सप्ताह तक',
    totalLoss: 'कुल कमी',
    week: 'सप्ताह',
    whatYoullGet: 'आपको क्या मिलेगा',
    chooseYourPlan: 'अपना प्लान चुनें',
    monthPlan1: '1 महीने का प्लान',
    monthPlan3: '3 महीने का प्लान',
    monthPlan6: '6 महीने का प्लान',
    mostPopular: 'सबसे लोकप्रिय',
    bestValue: 'सर्वश्रेष्ठ मूल्य',
    perDay: 'प्रति दिन',
    total: 'कुल',
    getMyPlan: 'मेरा प्लान लें',
    signInToContinue: 'जारी रखने के लिए साइन इन करें',
    processing: 'प्रोसेस हो रहा है...',
    moneyBack: '30 दिन की मनी-बैक गारंटी',
    peopleLove: 'लोग Myhealthyfy से प्यार करते हैं',
    retakeQuiz: 'क्विज़ फिर से लें',
    youreIn: 'आप शामिल हो गए!',
    activated: 'सक्रिय हो गया है',
    validUntil: 'तक वैध',
    goToDashboard: 'डैशबोर्ड पर जाएँ',
    paymentSimulated: 'भुगतान सिम्युलेटेड है (Razorpay कीज़ कॉन्फ़िगर नहीं हैं)। कीज़ जुड़ने पर वास्तविक शुल्क लागू होगा।',
    paymentFailedPrefix: 'भुगतान विफल:',
    // Feature list
    featurePersonalWorkout: 'व्यक्तिगत वर्कआउट प्लान',
    featureCustomNutrition: 'कस्टम पोषण गाइड',
    featureProgressTracking: 'प्रगति ट्रैकिंग',
    featureTrainerSupport: 'विशेषज्ञ ट्रेनर सहायता',
    featureAccess: '24/7 वर्कआउट एक्सेस',
    featureCommunity: 'समुदाय व प्रेरणा',
    // Auth
    createAccount: 'खाता बनाएँ',
    welcomeBack: 'फिर से स्वागत है',
    saveYourPlan: 'अपना प्लान सेव करें और प्रगति ट्रैक करें',
    signInToView: 'अपना प्लान देखने के लिए साइन इन करें',
    continueWithGoogle: 'Google से जारी रखें',
    or: 'या',
    namePlaceholder: 'नाम',
    emailPlaceholder: 'ईमेल',
    passwordPlaceholder: 'पासवर्ड',
    forgotPassword: 'पासवर्ड भूल गए?',
    pleaseWait: 'कृपया प्रतीक्षा करें...',
    signInBtn: 'साइन इन',
    createAccountBtn: 'खाता बनाएँ',
    alreadyHaveAccount: 'पहले से खाता है?',
    newHere: 'नए यहाँ?',
    somethingWentWrong: 'कुछ गलत हो गया',
    // Forgot / Reset password
    forgotTitle: 'पासवर्ड भूल गए?',
    forgotSub: 'अपना ईमेल दर्ज करें, हम आपके लिए एक सुरक्षित रीसेट पेज खोल देंगे।',
    yourEmailPlaceholder: 'आपका खाता ईमेल',
    rememberedIt: 'याद आ गया?',
    readyToReset: 'रीसेट के लिए तैयार',
    resetPrepared1: 'यदि खाता',
    resetPrepared2: 'मौजूद है और पासवर्ड का उपयोग करता है (Google साइन-इन नहीं), तो नीचे 1-घंटे का सुरक्षित लिंक तैयार है।',
    googleNoPassword: 'यदि आपने Google से साइन-अप किया है, तो साइन-इन पेज पर "Google से जारी रखें" का उपयोग करें — Google खातों में पासवर्ड नहीं होता।',
    resetLinkExpiry: 'यह लिंक 1 घंटे में समाप्त हो जाएगा और केवल एक बार उपयोग किया जा सकता है।',
    resetMyPassword: 'मेरा पासवर्ड रीसेट करें',
    resetPasswordTitle: 'पासवर्ड रीसेट करें',
    resetPasswordSub: 'अपने खाते के लिए नया पासवर्ड चुनें।',
    newPasswordPlaceholder: 'नया पासवर्ड',
    confirmPasswordPlaceholder: 'नया पासवर्ड कन्फ़र्म करें',
    passwordUpdated: 'पासवर्ड अपडेट हो गया',
    redirectingSignIn: 'साइन-इन पर रीडायरेक्ट किया जा रहा है...',
    signInNow: 'अभी साइन इन करें',
    updatePassword: 'पासवर्ड अपडेट करें',
    updating: 'अपडेट हो रहा है...',
    orBack: 'या',
    backToSignInLink: 'साइन इन पर वापस जाएँ',
    resetLinkMissingToken: 'इस रीसेट लिंक में टोकन नहीं है। कृपया नया अनुरोध करें।',
    passwordMin6: 'पासवर्ड कम से कम 6 अक्षरों का होना चाहिए।',
    passwordsDontMatch: 'पासवर्ड मेल नहीं खा रहे।',
    resetPasswordError: 'पासवर्ड रीसेट नहीं हो सका। कृपया पुनः प्रयास करें।',
    // Dashboard
    welcome: 'वापसी पर स्वागत है',
    yourSubscription: 'आपकी सदस्यता',
    active: 'सक्रिय',
    none: 'कोई नहीं',
    renewsOn: 'नवीनीकरण',
    paymentMock: 'भुगतान MOCK था (Razorpay कॉन्फ़िगर नहीं)',
    noActivePlan: 'कोई सक्रिय प्लान नहीं है।',
    takeQuizToGetOne: 'प्लान पाने के लिए क्विज़ लें',
    yourPlan: 'आपका प्लान',
    retakeQuizShort: 'क्विज़ फिर से लें',
    loading: 'लोड हो रहा है...',
    ageGroup: 'आयु समूह',
    gender: 'लिंग',
    bodyType: 'बॉडी टाइप',
    goal: 'लक्ष्य',
    currentWeight: 'वर्तमान वज़न',
    targetWeight: 'लक्षित वज़न',
    targetReduction: 'लक्षित कमी',
    noQuizOnFile: 'कोई क्विज़ रिकॉर्ड नहीं है।',
    takeTheQuiz: 'क्विज़ लें',
    unlockPlan: 'अपना 30-दिन का मील प्लान अनलॉक करें',
    unlockPlanSub: 'सब्सक्रिप्शन खरीदें और अपने व्यक्तिगत लक्ष्य, साप्ताहिक भोजन और वर्कआउट गाइड देखें।',
    unlockFeature1: 'व्यक्तिगत कैलोरी व प्रोटीन लक्ष्य',
    unlockFeature2: '30 दिन × प्रति दिन 8 भोजन तक (शाकाहारी + मांसाहारी)',
    unlockFeature3: 'वज़न घटाने व बढ़ाने के प्लान',
    purchasePlan: 'प्लान खरीदें',
    startsAt: '₹300 से शुरू · 30 दिन की मनी-बैक गारंटी',
    // MealPlan
    weightLoss: 'वज़न घटाना',
    weightGain: 'वज़न बढ़ाना',
    veg: 'शाकाहारी',
    nonVeg: 'मांसाहारी',
    avgLabel: 'औसत',
    kcalPerDay: 'kcal/दिन',
    yourDailyTargets: 'आपके व्यक्तिगत दैनिक लक्ष्य',
    calories: 'कैलोरी',
    protein: 'प्रोटीन',
    water: 'पानी',
    activity: 'गतिविधि',
    kcalPerDayShort: 'kcal/दिन',
    gPerDay: 'ग्राम/दिन',
    litresPerDay: 'लीटर/दिन',
    calculatedFrom: 'आपकी प्रोफ़ाइल से गणना:',
    prevWeek: 'पिछला सप्ताह',
    nextWeek: 'अगला सप्ताह',
    day: 'दिन',
    meals: 'भोजन',
    dailyTipsFor: 'आपकी',
    weightLossJourney: 'वज़न घटाने की यात्रा के लिए दैनिक सुझाव',
    weightGainJourney: 'वज़न बढ़ाने की यात्रा के लिए दैनिक सुझाव',
    workoutGuide: 'वर्कआउट गाइड',
    workoutGuideCaption: 'फुल-साइज़ खोलने के लिए इमेज पर टैप करें। बेहतरीन परिणाम के लिए इस 5-दिन स्प्लिट को अपने मील प्लान के साथ जोड़ें।',
    workoutGuideAlt: '5-दिन का वर्कआउट प्लान: छाती, पीठ, कंधे, बाजू, पैर',
    mealPlanDisclaimer: 'यह मील प्लान एक सामान्य गाइड है, चिकित्सा सलाह नहीं। पोर्शन साइज़ और मैक्रो को आपके वज़न, गतिविधि व आहार प्रतिबंधों के अनुसार समायोजित करें। पूरी तरह व्यक्तिगत प्लान के लिए पंजीकृत डाइटिशियन से परामर्श लें।',
    // Contact Us
    contactPre: 'हम आपसे सुनना पसंद करेंगे',
    contactTitle: 'संपर्क करें',
    contactSub: 'सवाल, प्रतिक्रिया, साझेदारी, या बस नमस्ते कहना — हमें संदेश भेजें और हम 1–2 कार्य दिवसों में जवाब देंगे।',
    messageSent: 'संदेश भेज दिया गया!',
    messageSentSub: 'संपर्क करने के लिए धन्यवाद। हम 1–2 कार्य दिवसों में आपके ईमेल पर उत्तर देंगे।',
    sendAnother: 'दूसरा संदेश भेजें',
    sendUsMessage: 'हमें संदेश भेजें',
    yourNamePlaceholder: 'आपका नाम',
    emailAddressPlaceholder: 'ईमेल पता',
    subject: 'विषय',
    subjectGeneral: 'सामान्य पूछताछ',
    subjectSupport: 'तकनीकी सहायता',
    subjectBilling: 'बिलिंग / रिफंड',
    subjectFeedback: 'प्रतिक्रिया / फ़ीचर अनुरोध',
    subjectPartnership: 'साझेदारी / प्रेस',
    subjectOther: 'अन्य',
    howCanWeHelp: 'हम आपकी कैसे मदद कर सकते हैं?',
    fillRequired: 'कृपया अपना नाम, ईमेल और संदेश भरें।',
    couldNotSend: 'आपका संदेश नहीं भेजा जा सका। कृपया पुनः प्रयास करें।',
    sending: 'भेजा जा रहा है...',
    sendMessage: 'संदेश भेजें',
    submitAgree: 'सबमिट करके, आप हमारी',
    emailUs: 'हमें ईमेल करें',
    phone: 'फ़ोन',
    phoneHours: 'सोम–शुक्र, सुबह 9 – शाम 6',
    address: 'पता',
    responseTime: 'प्रतिक्रिया समय',
    within12: '1–2 कार्य दिवसों के अंदर',
    priorityFor: 'सक्रिय ग्राहकों के लिए प्राथमिकता',
    faqTitle: 'अक्सर पूछे जाने वाले प्रश्न',
    // Language labels
    languageEn: 'EN',
    languageHi: 'हिं',
    // Quiz sub-steps
    height: 'कद',
    weight: 'वज़न',
    currentWeightLabel: 'वर्तमान वज़न:',
    enterTargetWeight: 'लक्षित वज़न दर्ज करें',
    enterYourAge: 'अपनी उम्र दर्ज करें',
    // Pregnancy plan
    pregnancy: 'गर्भावस्था',
    pregnancyDietPlan: '9-महीने का गर्भावस्था डाइट प्लान',
    pregnancyDietSubtitle: 'स्वस्थ गर्भावस्था के लिए मासिक पोषण गाइड',
    monthLabel: 'महीना',
    focusLabel: 'मुख्य ध्यान',
    dailySchedule: 'दैनिक भोजन तालिका',
    keyRecommendations: 'अवश्य शामिल करें',
    foodsToAvoidTitle: 'बचने योग्य भोजन',
    pregnancyDisclaimerLabel: 'चिकित्सा नोट',
    unlockFeature4: '9 महीने की मासिक गर्भावस्था डाइट गाइड',
    // AI meal tracker (HI)
    trackMealTitle: 'AI फ़ोटो कैलोरी काउंटर',
    trackMealSub: 'भोजन की फ़ोटो लें या अपलोड करें — AI कुछ ही सेकंड में कैलोरी व मैक्रो बता देगा',
    uploadPhoto: 'फ़ोटो अपलोड करें',
    takePhoto: 'फ़ोटो लें',
    analyzing: 'आपका भोजन विश्लेषण हो रहा है...',
    analyzeAgain: 'फिर से विश्लेषण करें',
    saveMeal: 'लॉग में सेव करें',
    cancel: 'रद्द करें',
    detected: 'पहचाना गया भोजन',
    ingredients: 'सामग्री',
    portion: 'पोर्शन',
    fatShort: 'फैट',
    carbs: 'कार्ब्स',
    confidence: 'AI विश्वास स्तर',
    todaysMeals: 'आज के भोजन',
    dailyTotals: 'दैनिक कुल',
    noMealsToday: 'आज कोई भोजन लॉग नहीं किया। ऊपर से अपना पहला भोजन स्नैप करें!',
    delete: 'हटाएँ',
    mealSaved: 'भोजन आपके लॉग में सेव हो गया',
    analyzeFailed: 'छवि का विश्लेषण नहीं हो सका। कृपया साफ़ फ़ोटो का उपयोग करें।',
    noFoodDetected: 'कोई भोजन नहीं मिला। कृपया अपने भोजन की स्पष्ट फ़ोटो का उपयोग करें।',
    imageTooLarge: 'छवि बहुत बड़ी है। कृपया 5 MB से कम फ़ाइल का उपयोग करें।',
    unlockFeature5: 'AI फ़ोटो कैलोरी काउंटर (भोजन की फ़ोटो लॉग करें)',
    // Diabetic plan (HI)
    diabetic: 'डायबिटीज़',
    diabeticDietPlan: '30-दिन का डायबिटिक (शुगर-फ्रेंडली) डाइट प्लान',
    diabeticDietSubtitle: 'ब्लड शुगर नियंत्रण हेतु कम-शुगर, अधिक फ़ाइबर भोजन',
    weekRotations: 'साप्ताहिक रोटेशन (सप्ताह 2–4)',
    foodsToInclude: 'शामिल करने योग्य भोजन',
    lifestyleTitle: 'जीवनशैली सुझाव',
    unlockFeature6: '30-दिन का डायबिटिक डाइट प्लान (कम-शुगर, डॉक्टर-फ्रेंडली)',
  },
};

const LangContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(() => {
    try {
      return localStorage.getItem('lang') || 'en';
    } catch {
      return 'en';
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('lang', lang);
    } catch {}
    try {
      document.documentElement.lang = lang;
    } catch {}
  }, [lang]);

  const t = useCallback(
    (key) => (strings[lang] && strings[lang][key]) || strings.en[key] || key,
    [lang]
  );

  // Pick language-aware value from an object like { en: 'Hello', hi: 'नमस्ते' } or a plain string
  const tr = useCallback(
    (obj) => {
      if (obj == null) return '';
      if (typeof obj === 'string') return obj;
      if (typeof obj === 'object') return obj[lang] || obj.en || '';
      return String(obj);
    },
    [lang]
  );

  const toggle = () => setLang((l) => (l === 'en' ? 'hi' : 'en'));

  return (
    <LangContext.Provider value={{ lang, setLang, t, tr, toggle }}>
      {children}
    </LangContext.Provider>
  );
};

export const useLang = () => {
  const ctx = useContext(LangContext);
  if (!ctx) throw new Error('useLang must be used within LanguageProvider');
  return ctx;
};
